import { Alert, Box, Button, Card, CardContent, CircularProgress, Stack, Typography } from '@mui/material';
import React from 'react';
import { useParams } from 'react-router-dom';
import { Breadcrumb } from 'src/components/Breadcrumb';
import PrivateLayoutView from 'src/Layout/PrivateLayout/PrivateLayoutView';
import { useContactCaptureRecordController } from '../../controllers';
import { getConversionRate } from '../../helpers';
import { ContactCapture } from '../../types';

const breadcrumbs = [
  { label: 'Contact Capture', path: '/contact-capture' },
  { label: 'Analytics', isActive: true },
];

const AnalyticsTotals = ({ record }: { record: ContactCapture }) => {
  const metrics = [
    { label: 'Views', value: record.views.toLocaleString() },
    { label: 'Contacts', value: record.contacts.toLocaleString() },
    { label: 'Conversion rate', value: getConversionRate(record.views, record.contacts) },
  ];

  return (
    <Box sx={{ overflow: 'auto', height: '100%' }}>
      <Typography variant="h5" fontWeight={600}>{record.name}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>
        Performance totals for this contact capture.
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
        {metrics.map(metric => (
          <Card variant="outlined" key={metric.label}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">{metric.label}</Typography>
              <Typography variant="h4" sx={{ mt: 1 }}>{metric.value}</Typography>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );
};

export const ContactCaptureAnalyticsView = () => {
  const { id } = useParams<{ id: string }>();
  const controller = useContactCaptureRecordController(id);

  const content = () => {
    if (controller.loading) {
      return (
        <Stack alignItems="center" justifyContent="center" height="100%">
          <CircularProgress aria-label="Loading analytics" />
        </Stack>
      );
    }
    if (controller.error) {
      return (
        <Alert
          severity="error"
          action={<Button color="inherit" onClick={() => void controller.retry()}>Retry</Button>}
        >
          {controller.error}
        </Alert>
      );
    }
    return controller.record ? <AnalyticsTotals record={controller.record} /> : null;
  };

  return (
    <PrivateLayoutView title={<Breadcrumb items={breadcrumbs} />}>
      {content()}
    </PrivateLayoutView>
  );
};
